/**
 * NCC: back-to-top button (sections/ncc-back-to-top.liquid, in the footer group so it is on every page).
 *
 * Shows once the page has scrolled the configured share of the viewport and glides back to the top with an
 * eased scroll of its own (native smooth scrolling is switched off on some systems, and its speed is not ours to
 * set). Horizon scrolls the desktop page inside .page-wrapper and phones scroll the window, so the scroller comes
 * from @theme/scroll-container. A wheel or touch during the glide hands control straight back to the visitor.
 * Whatever sits in its corner - the product page's sticky add-to-bag bar, the newsletter card, the cookie
 * banner - is measured on every update and the button lifts above it (--ncc-btt-lift, eased in the stylesheet).
 */
import { getScrollContainer } from '@theme/scroll-container';

const STICKY_BAR = '.sticky-add-to-cart__bar';
const OBSTACLES = 'ncc-newsletter-popup:not([hidden]) dialog[open], #shopify-pc__banner';
const CLEARANCE = 12;

const easeInOutCubic = (/** @type {number} */ t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

class NccBackToTop extends HTMLElement {
  frame = 0;
  glide = 0;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  settle;

  connectedCallback() {
    this.button = this.querySelector('button');
    this.threshold = (parseFloat(this.dataset.threshold || '100') || 100) / 100;
    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
      // the sticky bar and the banners animate in after the scroll: measure again once they have settled
      clearTimeout(this.settle);
      this.settle = setTimeout(() => this.update(), 450);
    };
    this.onResize = () => {
      this.bind();
      this.update();
    };
    this.stopGlide = () => this.cancelGlide();
    this.bind();
    window.addEventListener('resize', this.onResize);
    this.button?.addEventListener('click', () => this.scrollToTop());
    // the sticky bar's data-stuck flips from an IntersectionObserver, not a scroll frame: react at once
    new MutationObserver(() => this.onScroll()).observe(document.body, {
      childList: true,
      subtree: true,
      attributeFilter: ['open', 'hidden', 'data-stuck'],
    });
    this.update();
  }

  disconnectedCallback() {
    this.unbind();
    window.removeEventListener('resize', this.onResize);
    this.cancelGlide();
  }

  /** The element that scrolls: .page-wrapper on desktop, the document on phones and tablets. */
  bind() {
    this.unbind();
    this.scroller = getScrollContainer();
    this.eventTarget =
      this.scroller === document.scrollingElement || this.scroller === document.documentElement ? window : this.scroller;
    this.eventTarget.addEventListener('scroll', this.onScroll, { passive: true });
  }

  unbind() {
    this.eventTarget?.removeEventListener('scroll', this.onScroll);
  }

  get scrollTop() {
    return this.scroller?.scrollTop ?? 0;
  }

  update() {
    const visible = this.scrollTop > window.innerHeight * this.threshold;
    if (visible !== this.hasAttribute('data-visible')) this.toggleAttribute('data-visible', visible);
    this.clearObstacles();
  }

  /**
   * Lifts the button above anything fixed to the bottom edge that shares its column. Positions come from layout
   * (offsetTop of a fixed element ignores its transform), never from a rect caught mid-slide: the product page's
   * sticky bar slides in over 0.3s and reading it halfway moved the button in two steps (Miles, 2026-09-15).
   */
  clearObstacles() {
    if (!this.button) return;
    const own = this.button.getBoundingClientRect();
    const viewport = window.innerHeight;
    let lift = 0;
    /** @param {number} top @param {number} left @param {number} right */
    const consider = (top, left, right) => {
      if (top >= viewport || right <= own.left || left >= own.right) return;
      lift = Math.max(lift, Math.round(viewport - top) + CLEARANCE);
    };

    const bar = document.querySelector(STICKY_BAR);
    if (
      bar instanceof HTMLElement &&
      bar.dataset.stuck === 'true' &&
      bar.closest('sticky-add-to-cart')?.getAttribute('data-variant-available') === 'true'
    ) {
      const rect = bar.getBoundingClientRect();
      consider(bar.offsetTop, rect.left, rect.right);
    }

    for (const element of document.querySelectorAll(OBSTACLES)) {
      if (!(element instanceof HTMLElement)) continue;
      const rect = element.getBoundingClientRect();
      if (!rect.height) continue;
      const fixed = element.offsetParent === null && getComputedStyle(element).position === 'fixed';
      consider(fixed ? element.offsetTop : rect.top, rect.left, rect.right);
    }

    const value = lift ? `${lift}px` : '';
    if (this.style.getPropertyValue('--ncc-btt-lift') !== value) this.style.setProperty('--ncc-btt-lift', value);
  }

  scrollToTop() {
    const start = this.scrollTop;
    if (!this.scroller || start <= 0) return;

    if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.scroller.scrollTo({ top: 0, behavior: 'instant' });
      return;
    }

    this.cancelGlide();
    // longer pages take a little longer, within reason: 450ms for a short hop, 900ms at most
    const duration = Math.min(900, Math.max(450, start * 0.3));
    const began = performance.now();
    const step = (/** @type {number} */ now) => {
      const progress = Math.min(1, (now - began) / duration);
      // behavior: instant - the page container has scroll-behavior: smooth, which re-smoothed every frame's
      // position and made the glide wait ~700ms before rushing (Miles, 2026-09-15)
      if (this.scroller) this.scroller.scrollTo({ top: Math.round(start * (1 - easeInOutCubic(progress))), behavior: 'instant' });
      if (progress < 1) {
        this.glide = requestAnimationFrame(step);
      } else {
        this.cancelGlide();
        this.button?.blur();
      }
    };
    this.glide = requestAnimationFrame(step);
    for (const type of ['wheel', 'touchstart', 'keydown']) {
      this.eventTarget?.addEventListener(type, this.stopGlide, { passive: true, once: true });
    }
  }

  cancelGlide() {
    if (this.glide) cancelAnimationFrame(this.glide);
    this.glide = 0;
    for (const type of ['wheel', 'touchstart', 'keydown']) {
      this.eventTarget?.removeEventListener(type, this.stopGlide);
    }
  }
}

if (!customElements.get('ncc-back-to-top')) {
  customElements.define('ncc-back-to-top', NccBackToTop);
}
